import React, { useEffect, useState } from "react";
import PortalLayout, { usePortal } from "../components/PortalLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { portalApi, adminApi } from "../lib/portalApi";
import { toast } from "sonner";
import { Inbox, SendHorizontal, MessageSquare, UserCircle2, ShieldCheck } from "lucide-react";

const PRIORITY_CLS = {
  Low:     "text-cream-dim border-steel",
  Normal:  "text-electric border-electric",
  High:    "text-gold border-gold",
  Urgent:  "text-[color:var(--auditiq-critical)] border-[color:var(--auditiq-critical)]",
};

const STATUS_CLS = {
  Open:         "text-electric",
  "In Review":  "text-gold",
  "In Progress":"text-gold",
  Resolved:     "text-[color:var(--auditiq-success)]",
  Rejected:     "text-[color:var(--auditiq-critical)]",
  Closed:       "text-cream-dim",
};

const Inner = () => {
  const { me } = usePortal();
  const isAdmin = me?.role === "super_admin";
  const [mine, setMine] = useState([]);
  const [adminRows, setAdminRows] = useState([]);
  const [adminCounts, setAdminCounts] = useState({});
  const [meta, setMeta] = useState({ statuses: [], priorities: [], categories: [] });
  const [form, setForm] = useState({ subject: "", category: "Feature Request", priority: "Normal", description: "" });
  const [activeTab, setActiveTab] = useState(isAdmin ? "queue" : "submit");
  const [respondFor, setRespondFor] = useState(null);
  const [respondText, setRespondText] = useState("");
  const [respondStatus, setRespondStatus] = useState("In Review");

  const loadMine = async () => {
    const r = await portalApi.ticketsMine();
    setMine(r.tickets || []);
    setMeta({ statuses: r.statuses, priorities: r.priorities, categories: r.categories });
  };
  const loadAdmin = async () => {
    if (!isAdmin) return;
    const r = await adminApi.ticketsList();
    setAdminRows(r.tickets || []);
    setAdminCounts(r.counts || {});
    setMeta({ statuses: r.statuses, priorities: r.priorities, categories: r.categories });
  };

  useEffect(() => { loadMine(); loadAdmin(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    try {
      await portalApi.ticketsCreate(form);
      toast.success("Ticket submitted");
      setForm({ subject: "", category: "Feature Request", priority: "Normal", description: "" });
      setActiveTab("mine");
      loadMine(); loadAdmin();
    } catch (err) { toast.error(err?.response?.data?.detail || "Submit failed"); }
  };

  const respond = async () => {
    if (!respondFor) return;
    try {
      await adminApi.ticketsUpdate(respondFor.id, { status: respondStatus, response: respondText });
      toast.success(`Ticket ${respondFor.ticket_number} updated`);
      setRespondFor(null); setRespondText(""); setRespondStatus("In Review");
      loadAdmin();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const tabs = [
    { id: "submit", label: "Submit Ticket", show: true },
    { id: "mine",   label: `My Tickets (${mine.length})`, show: true },
    ...(isAdmin ? [{ id: "queue", label: `Admin Queue (${adminRows.length})`, show: true }] : []),
  ];

  return (
    <div className="space-y-6" data-testid="portal-tickets">
      <div>
        <div className="text-[11px] uppercase tracking-[0.28em] text-electric flex items-center gap-2">
          <Inbox size={12} /> Requests & Support
        </div>
        <h1 className="font-display text-3xl md:text-4xl mt-2">Ticket Queue</h1>
        <p className="text-cream-dim text-sm mt-2 max-w-2xl">
          Submit feature requests, bug reports, data requests, or access issues. AuditIQ administrators respond to all tickets in chronological priority order.
        </p>
      </div>

      {/* Tab bar */}
      <div className="flex border-b border-gold-dim/20 gap-1">
        {tabs.filter(t => t.show).map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            data-testid={`tickets-tab-${t.id}`}
            className={`px-4 py-2.5 text-xs uppercase tracking-[0.18em] transition-colors ${
              activeTab === t.id
                ? "text-gold border-b-2 border-gold"
                : "text-cream-dim hover:text-gold"
            }`}>{t.label}</button>
        ))}
      </div>

      {/* Submit */}
      {activeTab === "submit" && (
        <form onSubmit={submit} className="glass p-6 grid md:grid-cols-2 gap-4" data-testid="ticket-submit-form">
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-[0.22em] text-steel">Subject</label>
            <Input required minLength={3} value={form.subject}
              onChange={e => setForm({ ...form, subject: e.target.value })}
              className="bg-navy-deep border-gold-dim/40 mt-1"
              placeholder="Short summary of the request" data-testid="ticket-subject" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.22em] text-steel">Category</label>
            <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
              <SelectTrigger className="bg-navy-deep border-gold-dim/40 text-cream mt-1" data-testid="ticket-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-navy-soft border-gold-dim/40 text-cream">
                {(meta.categories || []).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.22em] text-steel">Priority</label>
            <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
              <SelectTrigger className="bg-navy-deep border-gold-dim/40 text-cream mt-1" data-testid="ticket-priority">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-navy-soft border-gold-dim/40 text-cream">
                {(meta.priorities || []).map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-[0.22em] text-steel">Description</label>
            <Textarea required minLength={5} rows={6}
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              className="bg-navy-deep border-gold-dim/40 mt-1 text-cream"
              placeholder="Describe the request in detail. Include steps, URLs, or expected behavior."
              data-testid="ticket-description" />
          </div>
          <div className="md:col-span-2 flex justify-end">
            <Button type="submit" className="btn-gold h-11 px-6" data-testid="ticket-submit-btn">
              <SendHorizontal size={14} className="mr-2" /> Submit Ticket
            </Button>
          </div>
        </form>
      )}

      {/* My tickets */}
      {activeTab === "mine" && (
        <div className="space-y-3" data-testid="ticket-mine-list">
          {mine.length === 0 && (
            <div className="glass p-10 text-center text-cream-dim italic">
              You haven&apos;t submitted any tickets yet.
            </div>
          )}
          {mine.map(t => (
            <article key={t.id} className="glass p-5" data-testid={`ticket-card-${t.ticket_number}`}>
              <div className="flex items-start justify-between flex-wrap gap-2">
                <div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-gold font-mono text-xs">{t.ticket_number}</span>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${PRIORITY_CLS[t.priority]}`}>
                      {t.priority}
                    </span>
                    <span className={`text-[10px] uppercase tracking-wider ${STATUS_CLS[t.status]}`}>
                      {t.status}
                    </span>
                  </div>
                  <h3 className="text-cream text-lg mt-2">{t.subject}</h3>
                  <div className="text-[11px] text-steel mt-1">{t.category} · {new Date(t.created_at).toLocaleString()}</div>
                </div>
              </div>
              <p className="text-cream-dim text-sm mt-3 whitespace-pre-wrap">{t.description}</p>
              {(t.responses || []).length > 0 && (
                <div className="mt-4 border-t border-gold-dim/15 pt-3 space-y-2">
                  {t.responses.map((r, i) => (
                    <div key={i} className="flex gap-3 text-sm">
                      <ShieldCheck size={14} className="text-electric shrink-0 mt-1" />
                      <div>
                        <div className="text-[10px] text-steel uppercase tracking-wider">
                          {r.by} · {new Date(r.at).toLocaleString()} · set to {r.status_set}
                        </div>
                        <div className="text-cream mt-0.5 whitespace-pre-wrap">{r.note}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      {/* Admin queue */}
      {activeTab === "queue" && isAdmin && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            {(meta.statuses || []).map(s => (
              <div key={s} className="glass p-3 text-center">
                <div className="text-[10px] uppercase tracking-[0.2em] text-steel">{s}</div>
                <div className="font-display text-xl text-gold mt-1">{adminCounts[s] || 0}</div>
              </div>
            ))}
          </div>

          <div className="space-y-3" data-testid="ticket-admin-list">
            {adminRows.length === 0 && (
              <div className="glass p-10 text-center text-cream-dim italic">No tickets yet.</div>
            )}
            {adminRows.map(t => (
              <article key={t.id} className="glass p-5" data-testid={`ticket-admin-${t.ticket_number}`}>
                <div className="flex items-start justify-between flex-wrap gap-2">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-gold font-mono text-xs">{t.ticket_number}</span>
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${PRIORITY_CLS[t.priority]}`}>
                        {t.priority}
                      </span>
                      <span className={`text-[10px] uppercase tracking-wider ${STATUS_CLS[t.status]}`}>
                        {t.status}
                      </span>
                    </div>
                    <h3 className="text-cream text-lg mt-2">{t.subject}</h3>
                    <div className="text-[11px] text-steel mt-1 flex items-center gap-2">
                      <UserCircle2 size={12} /> {t.submitted_by_name} &lt;{t.submitted_by_email}&gt;
                      <span>·</span> {t.category}
                      <span>·</span> {new Date(t.created_at).toLocaleString()}
                    </div>
                  </div>
                  <Button onClick={() => { setRespondFor(t); setRespondStatus(t.status); setRespondText(""); }}
                    className="btn-ghost-gold h-9 px-4 text-xs"
                    data-testid={`ticket-respond-${t.ticket_number}`}>
                    <MessageSquare size={12} className="mr-1.5" /> Respond
                  </Button>
                </div>
                <p className="text-cream-dim text-sm mt-3 whitespace-pre-wrap">{t.description}</p>
                {(t.responses || []).length > 0 && (
                  <div className="mt-4 border-t border-gold-dim/15 pt-3 space-y-2">
                    {t.responses.map((r, i) => (
                      <div key={i} className="text-sm">
                        <div className="text-[10px] text-steel uppercase tracking-wider">
                          {r.by} · {new Date(r.at).toLocaleString()} · set to {r.status_set}
                        </div>
                        <div className="text-cream mt-0.5 whitespace-pre-wrap">{r.note}</div>
                      </div>
                    ))}
                  </div>
                )}

                {respondFor?.id === t.id && (
                  <div className="mt-4 border-t border-gold-dim/20 pt-4 space-y-3" data-testid="ticket-respond-panel">
                    <div className="grid md:grid-cols-[180px_1fr] gap-3">
                      <Select value={respondStatus} onValueChange={setRespondStatus}>
                        <SelectTrigger className="bg-navy-deep border-gold-dim/40 text-cream">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-navy-soft border-gold-dim/40 text-cream">
                          {(meta.statuses || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Textarea rows={3} value={respondText} onChange={e => setRespondText(e.target.value)}
                        placeholder="Response note (optional)"
                        className="bg-navy-deep border-gold-dim/40 text-cream"
                        data-testid="ticket-respond-textarea" />
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button onClick={() => setRespondFor(null)} className="btn-ghost-gold h-9 px-4 text-xs">Cancel</Button>
                      <Button onClick={respond} className="btn-gold h-9 px-5 text-xs" data-testid="ticket-respond-save">
                        Save Response
                      </Button>
                    </div>
                  </div>
                )}
              </article>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const PortalTickets = () => <PortalLayout><Inner /></PortalLayout>;
export default PortalTickets;
